import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Linking,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { Text, Card, Button, Avatar, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Heart,
  MapPin,
  Calendar,
  BookOpen,
  Users,
  MessageCircle,
  Instagram,
  Youtube,
  Globe,
  CheckCircle2,
  Trophy,
  Medal,
  Music,
  Star,
  Eye,
} from 'lucide-react-native';
import { Toast } from '../utils/toast';
import { HeaderActions } from '../components/ui/HeaderActions';
import { logger } from '../utils/logger';
import { creatorApi, Creator } from '../services/api/creatorApi';

export function CreatorProfileScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const { creatorId } = route.params as { creatorId: string };

  const [creator, setCreator] = useState<Creator | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCreator = async () => {
    try {
      setError(null);
      const data = await creatorApi.getCreatorById(creatorId);
      setCreator(data);
    } catch (err: any) {
      logger.error('Error fetching creator:', err.message);
      setError('Failed to load creator profile. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCreator();
  }, [creatorId]);

  useEffect(() => {
    // Set up navigation header with share and heart functionality
    if (creator) {
      navigation.setOptions({
        headerRight: () => (
          <HeaderActions
            isLiked={isFollowing}
            onLikePress={handleFollow}
            shareContent={{
              title: `${creator.firstName} ${creator.lastName}`,
              message: `Check out this amazing coffee creator: ${creator.firstName} ${creator.lastName}\n\n${creator.bio}`,
              url: `https://coffeeplexity.com/creators/${creator.id}`,
            }}
            testID='creator-header-actions'
          />
        ),
      });
    }
  }, [creator, navigation, isFollowing]);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchCreator();
  };

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
  };

  const validateYouTubeUrl = (url: string): boolean => {
    // Basic validation for YouTube URLs
    const youtubePatterns = [
      /^https?:\/\/(www\.)?youtube\.com\/@[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/channel\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/c\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
      /^https?:\/\/(www\.)?youtu\.be\/[\w-]+/,
    ];

    return youtubePatterns.some(pattern => pattern.test(url));
  };

  const handleSocialLink = (url: string) => {
    if (url) {
      try {
        // Validate and clean the URL
        const cleanUrl = url.trim();

        // Handle YouTube URLs more gracefully
        if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
          // Validate YouTube URL format
          if (!validateYouTubeUrl(cleanUrl)) {
            Toast.show('Invalid YouTube URL format', 'error');
            return;
          }

          // For channel URLs, always open in browser to avoid app issues
          if (
            cleanUrl.includes('/@') ||
            cleanUrl.includes('/channel/') ||
            cleanUrl.includes('/c/')
          ) {
            logger.log('Opening YouTube channel:', cleanUrl);
            Linking.openURL(cleanUrl).catch(error => {
              logger.error('Error opening YouTube channel:', error);
              Toast.show('Unable to open YouTube. Please try again.', 'error');
            });
          } else {
            // For video URLs, try to open with YouTube app first, fallback to browser
            logger.log('Opening YouTube video:', cleanUrl);
            Linking.canOpenURL(cleanUrl)
              .then(supported => {
                if (supported) {
                  Linking.openURL(cleanUrl).catch(error => {
                    logger.error('Error opening YouTube video:', error);
                    Toast.show(
                      'Unable to open YouTube video. Please try again.',
                      'error'
                    );
                  });
                } else {
                  // Fallback to opening in browser
                  const browserUrl = cleanUrl.includes('youtube.com')
                    ? cleanUrl
                    : `https://youtube.com/watch?v=${cleanUrl.split('youtu.be/')[1]}`;
                  logger.log('Opening YouTube in browser:', browserUrl);
                  Linking.openURL(browserUrl).catch(error => {
                    logger.error('Error opening YouTube in browser:', error);
                    Toast.show(
                      'Unable to open YouTube. Please try again.',
                      'error'
                    );
                  });
                }
              })
              .catch(() => {
                // If all else fails, open in browser
                logger.log('Opening YouTube fallback in browser:', cleanUrl);
                Linking.openURL(cleanUrl).catch(error => {
                  logger.error('Error opening YouTube fallback:', error);
                  Toast.show(
                    'Unable to open YouTube. Please try again.',
                    'error'
                  );
                });
              });
          }
        } else {
          // For non-YouTube links, use standard linking
          logger.log('Opening social link:', cleanUrl);
          Linking.openURL(cleanUrl).catch(error => {
            logger.error('Error opening social link:', error);
            Toast.show('Unable to open link. Please try again.', 'error');
          });
        }
      } catch (error) {
        logger.error('Error opening social link:', error);
        Toast.show('Unable to open link. Please try again.', 'error');
      }
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Loading state
  if (loading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text variant='bodyMedium' style={{ marginTop: 12 }}>
            Loading creator profile...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (error) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <Text variant='titleMedium' style={{ color: theme.colors.error }}>
            {error}
          </Text>
          <Button
            mode='contained'
            onPress={fetchCreator}
            style={{ marginTop: 16 }}
          >
            Retry
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  // Creator not found
  if (!creator) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <Text variant='titleMedium'>Creator not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Creator Profile Header */}
        <View
          style={[styles.header, { backgroundColor: theme.colors.surface }]}
        >
          <View style={styles.creatorInfo}>
            <Avatar.Image
              size={100}
              source={{ uri: creator.avatar }}
              style={styles.avatar}
            />
            <View style={styles.creatorDetails}>
              <View style={styles.nameRow}>
                <Text
                  variant='headlineMedium'
                  style={[styles.name, { flexShrink: 1, flexWrap: 'wrap' }]}
                  numberOfLines={2}
                >
                  {creator.firstName} {creator.lastName}
                </Text>
                {creator.isVerified && (
                  <CheckCircle2
                    size={16}
                    color={theme.colors.onSurfaceVariant}
                  />
                )}
              </View>

              <View style={styles.locationRow}>
                <MapPin size={16} color={theme.colors.onSurfaceVariant} />
                <Text variant='bodyMedium' style={styles.location}>
                  @{creator.username}
                </Text>
              </View>

              {/* Social Media Icons */}
              <View style={styles.socialIconsContainer}>
                {creator.socialLinks.instagram && (
                  <Button
                    mode='text'
                    onPress={() =>
                      handleSocialLink(creator.socialLinks.instagram)
                    }
                    style={styles.socialIconButton}
                    icon={() => <Instagram size={20} color='#E4405F' />}
                  >
                    {''}
                  </Button>
                )}

                {creator.socialLinks.youtube && (
                  <Button
                    mode='text'
                    onPress={() =>
                      handleSocialLink(creator.socialLinks.youtube)
                    }
                    style={styles.socialIconButton}
                    icon={() => <Youtube size={20} color='#FF0000' />}
                  >
                    {''}
                  </Button>
                )}

                {creator.socialLinks.tiktok && (
                  <Button
                    mode='text'
                    onPress={() => handleSocialLink(creator.socialLinks.tiktok)}
                    style={styles.socialIconButton}
                    icon={() => <Music size={20} color='#FE2C55' />}
                  >
                    {''}
                  </Button>
                )}

                {creator.socialLinks.website && (
                  <Button
                    mode='text'
                    onPress={() =>
                      handleSocialLink(creator.socialLinks.website)
                    }
                    style={styles.socialIconButton}
                    icon={() => (
                      <Globe size={20} color={theme.colors.primary} />
                    )}
                  >
                    {''}
                  </Button>
                )}
              </View>
            </View>
          </View>

          <View style={styles.followButtonContainer}>
            <Button
              mode={isFollowing ? 'outlined' : 'contained'}
              onPress={handleFollow}
              style={[
                styles.followButton,
                {
                  borderRadius: 18,
                  height: 36,
                },
              ]}
              contentStyle={{
                height: 36,
                paddingHorizontal: 16,
              }}
              labelStyle={{
                fontSize: 14,
                fontWeight: '600',
                letterSpacing: 0.1,
              }}
              textColor={
                isFollowing ? theme.colors.primary : theme.colors.onPrimary
              }
            >
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
            <Button
              mode='outlined'
              onPress={() =>
                (navigation as any).navigate('creatorRecipes', {
                  creatorId: creator.id,
                  creatorName: creator.firstName,
                })
              }
              style={[
                styles.followButton,
                {
                  borderRadius: 18,
                  height: 36,
                },
              ]}
              contentStyle={{
                height: 36,
                paddingHorizontal: 16,
              }}
              labelStyle={{
                fontSize: 14,
                fontWeight: '600',
                letterSpacing: 0.1,
              }}
              icon='book-open-variant'
            >
              View {creator.firstName}'s Recipes
            </Button>
          </View>
        </View>

        {/* Bio Section */}
        <Card
          style={[styles.bioCard, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              About
            </Text>
            <Text variant='bodyMedium' style={styles.bio}>
              {creator.bio}
            </Text>
          </Card.Content>
        </Card>

        {/* Specialties Section */}
        <Card
          style={[
            styles.specialtiesCard,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Specialties
            </Text>
            <View style={styles.specialtiesContainer}>
              {creator.specialties.map((specialty: string, index: number) => (
                <View
                  key={index}
                  style={[styles.specialtyChip, { backgroundColor: '#f3f4f6' }]}
                >
                  <Text
                    style={{
                      color: '#374151',
                      fontSize: 12,
                      fontWeight: '500',
                      letterSpacing: 0.4,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                    }}
                  >
                    {specialty
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                </View>
              ))}
            </View>
          </Card.Content>
        </Card>

        {/* Stats Section */}
        <Card
          style={[styles.statsCard, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Statistics
            </Text>
            <View style={styles.statsGrid}>
              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <Users size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {formatNumber(creator.followers)}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Followers
                </Text>
              </View>

              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <BookOpen size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {creator.recipes}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Recipes
                </Text>
              </View>

              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <Star size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {creator.rating}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Rating
                </Text>
              </View>

              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <MessageCircle
                    size={20}
                    color={theme.colors.onSurfaceVariant}
                  />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {formatNumber(creator.reviewCount)}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Reviews
                </Text>
              </View>

              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <Heart size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {formatNumber(creator.likes)}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Likes
                </Text>
              </View>

              <View style={styles.statItem}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                  ]}
                >
                  <Eye size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                <Text variant='headlineSmall' style={styles.statValue}>
                  {formatNumber(creator.views)}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Views
                </Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Professional Credentials */}
        <Card
          style={[
            styles.credentialsCard,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Professional Credentials
            </Text>

            {creator.certifications.length > 0 && (
              <View style={styles.credentialSection}>
                <Text variant='titleSmall' style={styles.credentialTitle}>
                  Certifications
                </Text>
                {creator.certifications.map((cert: string, index: number) => (
                  <View key={index} style={styles.credentialItem}>
                    <View
                      style={[
                        styles.credentialIconContainer,
                        {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          width: 28,
                          height: 28,
                          borderRadius: 14,
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Medal size={16} color='#4CAF50' />
                    </View>
                    <Text variant='bodyMedium' style={styles.credentialText}>
                      {cert}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {creator.awards.length > 0 && (
              <View style={styles.credentialSection}>
                <Text variant='titleSmall' style={styles.credentialTitle}>
                  Awards
                </Text>
                {creator.awards.map((award: string, index: number) => (
                  <View key={index} style={styles.credentialItem}>
                    <View
                      style={[
                        styles.credentialIconContainer,
                        {
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          width: 28,
                          height: 28,
                          borderRadius: 14,
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Trophy size={16} color='#FFD700' />
                    </View>
                    <Text variant='bodyMedium' style={styles.credentialText}>
                      {award}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </Card.Content>
        </Card>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    paddingBottom: 24,
  },
  creatorInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  avatar: {
    marginBottom: 16,
  },
  creatorDetails: {
    alignItems: 'center',
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  name: {
    fontWeight: 'bold',
    marginRight: 8,
    flex: 1,
    flexShrink: 1,
  },
  verifiedBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  location: {
    marginLeft: 4,
    opacity: 0.7,
  },

  followButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    gap: 12,
  },
  followButton: {
    // Button will size to content
  },
  bioCard: {
    margin: 16,
  },
  bio: {
    lineHeight: 22,
    marginTop: 8,
  },
  specialtiesCard: {
    margin: 16,
    marginTop: 0,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  specialtiesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  specialtyChip: {
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  statsCard: {
    margin: 16,
    marginTop: 0,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginTop: 8,
    gap: 16,
  },
  statItem: {
    alignItems: 'center',
    width: '30%',
    marginBottom: 16,
  },
  statValue: {
    fontWeight: 'bold',
    marginTop: 4,
    marginBottom: 2,
  },
  statLabel: {
    opacity: 0.7,
    textAlign: 'center',
  },
  credentialsCard: {
    margin: 16,
    marginTop: 0,
  },
  credentialSection: {
    marginBottom: 16,
  },
  credentialTitle: {
    fontWeight: '600',
    marginBottom: 8,
    opacity: 0.8,
  },
  credentialItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  credentialText: {
    marginLeft: 8,
    flex: 1,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  ratingIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  credentialIconContainer: {
    marginRight: 12,
  },

  featuredBadge: {
    marginLeft: 8,
  },
  bottomButton: {
    padding: 16,
    paddingBottom: 32,
  },
  viewRecipesButton: {
    width: '100%',
  },
  socialIconsContainer: {
    flexDirection: 'row',
    marginTop: 12,
    gap: 12,
  },
  socialIconButton: {
    padding: 0,
    minWidth: 40,
    alignItems: 'center',
  },
});
